/**
 * 房间信息
 */
import React, { useMemo, useRef, memo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import ContentBlock from '@/components/Reservation/ContentBlock';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import CouponSelector, { CouponSelectorRef } from '@/components/Reservation/CouponSelector';
import CouponGapText from '@/components/Reservation/CouponGapText';
import { RESERVATION_SCENE } from '@/config/constant';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import {
  createStateSelector,
  findItemByKey,
  getEmptyCouponData,
} from '@/redux/actions/reservation/helper';
import {
  handleRentItemChange,
  batchUpdateRoomCoupon,
  getMainState,
} from '@/redux/actions/reservation/checkInEditor';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkInEditor';
import {
  CouponValue,
  EditRoomPriceInfo,
  EditRentRoomInfo,
} from '@/redux/reducers/reservation/base.d';
import CouponService from '@/components/Reservation/CouponService';
import useUpdate from '@/hooks/useUpdate';
import BatchCouponButton, {
  IBatchCouponParams,
} from '@/components/Reservation/BatchSelectCouponButton';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const {
    rentRoomInfos,
    originCouponList,
    rentTimeInfo,
    channelList,
    commonInfo,
    channelInfo,
  } = state;
  return {
    rentRoomInfos,
    mainUserId: commonInfo.ownerWYId,
    occType: rentTimeInfo.occType,
    channelList,
    sourceChannelId: channelInfo.channelId,
    originCouponList,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callRentItemChange: handleRentItemChange,
  callBatchUpdateCoupon: batchUpdateRoomCoupon,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    rentRoomInfos,
    mainUserId,
    occType,
    channelList,
    sourceChannelId,
    originCouponList,
    callRentItemChange,
    callBatchUpdateCoupon,
    callGetState,
  } = props;
  const couponSelectorRef = useRef<CouponSelectorRef>(null);

  const forceUpdate = useUpdate();

  const couponService = useMemo(() => {
    return new CouponService();
  }, []);

  useEffect(() => {
    // 获取当前用户可用券
    couponService.initCouponList(mainUserId).then(forceUpdate);
  }, [mainUserId]);

  // 当前渠道类型
  const currentChannelType = useMemo(() => {
    const channelItem = findItemByKey(channelList, 'id', sourceChannelId);
    return channelItem?.type;
  }, [channelList, sourceChannelId]);

  // 更新券状态
  const updateCouponService = () => {
    try {
      // 恢复券状态
      couponService.resetWYListStatus();
      // 解锁订单券
      couponService.batchUnlockWYCoupon(originCouponList);
      const lockList = [];
      rentRoomInfos.forEach((roomItem) => {
        (roomItem.roomPriceList || []).forEach((rentDay) => {
          if (rentDay.couponNo) {
            lockList.push(rentDay.couponNo);
          }
        });
      });
      // 批量锁定券
      couponService.batchLockWYCoupon(lockList);
    } catch (e) {
      //
    }
  };
  updateCouponService();

  // 房间号
  const renderRoomNo: (roomNo: string) => React.ReactNode = (roomNo) => {
    return roomNo || intl.get('app_reservation_room_queue_no');
  };

  // 押金
  const handleDepositChange: (deposit: string, record: EditRentRoomInfo) => void = (
    deposit,
    record,
  ) => {
    // if (PRICE_REG.test(deposit)) {
    callRentItemChange(record.indexId, { deposit });
    // }
  };

  const handlePriceChange: (
    text: string,
    dayPrice: EditRoomPriceInfo,
    record: EditRentRoomInfo,
  ) => void = (text, dayPrice, record) => {
    // if (PRICE_REG.test(text)) {
    const { rentDateId } = dayPrice;
    callRentItemChange(record.indexId, {
      roomPriceList: (record.roomPriceList || []).map((item) => {
        if (item.rentDateId === rentDateId) {
          return {
            ...item,
            // 价格修改 删除已选券
            ...getEmptyCouponData(),
            inputPrice: text,
          };
        }
        return item;
      }),
    });
    // }
  };

  // 押金修改
  const renderDepositItem: (deposit: string, record: EditRentRoomInfo) => React.ReactNode = (
    deposit,
    record,
  ) => {
    return (
      <InputWithCoupon
        withCoupon={false}
        value={deposit}
        onChange={(val) => handleDepositChange(val, record)}
      />
    );
  };

  // 选择优惠券
  const showCouponSelector: (dayPrice: EditRoomPriceInfo, record: EditRentRoomInfo) => void = (
    dayPrice,
    record,
  ) => {
    const { inputPrice, couponNo, rentDateId } = dayPrice;
    const lockCouponList = []; // 锁定的券列表
    const originCouponNoMap = {}; // 原始券 需要解锁掉
    originCouponList.forEach((itemNo) => {
      originCouponNoMap[itemNo] = true;
    });

    rentRoomInfos.forEach((roomInfo) => {
      (roomInfo.roomPriceList || []).forEach((priceInfo) => {
        const itemNo = priceInfo.couponNo;
        // 当天没选券 或者 当天已过夜审
        if (!itemNo || priceInfo.isBeforeAudit) return;
        lockCouponList.push(itemNo);
        // 这个券当前还是选中状态 不需要解锁
        delete originCouponNoMap[itemNo];
      });
    });

    couponSelectorRef.current.openModal({
      wyuid: mainUserId,
      sceneType: RESERVATION_SCENE.CheckInModify,
      amount: parseYuan2Fen(inputPrice),
      occType,
      changedCouponId: couponNo ? [couponNo] : [],
      unlockCouponList: (couponNo ? [couponNo] : []).concat(Object.keys(originCouponNoMap)),
      lockCouponList,
      channelType: currentChannelType,
      businessDate: dayPrice.rentDate,
      callback: (couponNoList, couponInfoList) => {
        // 更新手工券
        couponService.refreshManualList().then(forceUpdate);
        let updateCouponInfo: CouponValue = getEmptyCouponData();
        if (couponNoList.length) {
          const [targetDetail] = couponInfoList;
          const { couponFaceValue, couponName, couponType, wyuid, couponSubType } = targetDetail;
          updateCouponInfo = {
            couponValue: parseFen2Yuan(couponFaceValue),
            couponAmount: couponFaceValue,
            couponName,
            couponNo: targetDetail.couponNo,
            couponType,
            couponWyuid: wyuid,
            couponSubType,
          };
        }
        callRentItemChange(record.indexId, {
          roomPriceList: (record.roomPriceList || []).map((item) => {
            if (item.rentDateId === rentDateId) {
              return { ...item, ...updateCouponInfo };
            }
            return item;
          }),
        });
      },
    });
  };

  const renderPriceItem: (
    dayPrice: EditRoomPriceInfo,
    record: EditRentRoomInfo,
  ) => React.ReactNode = (dayPrice, record) => {
    const { inputPrice, couponValue, isBeforeAudit, couponNo } = dayPrice;
    let isInputDisabled = false;
    if (isBeforeAudit) {
      // 时间早已当前所在营业日 时 不可编辑价格
      isInputDisabled = true;
    }
    // 券是否可用
    const isCouponDisabled = couponService.isCouponDisabled({
      priceChannel: currentChannelType,
      exclude: [couponNo],
    });

    return (
      <InputWithCoupon
        withCoupon
        couponValue={couponValue}
        value={inputPrice}
        inputDisabled={isInputDisabled}
        couponDisabled={isInputDisabled || isCouponDisabled}
        showCouponSelector={() => showCouponSelector(dayPrice, record)}
        onChange={(val) => handlePriceChange(val, dayPrice, record)}
      />
    );
  };

  const getTableInfo = () => {
    const leftColumns: ColumnsType<EditRentRoomInfo> = [
      {
        title: intl.get('app_constants_room_type'),
        dataIndex: 'roomTypeName',
        width: 232,
        fixed: 'left',
      },
      {
        title: intl.get('app_constants_room_no'),
        dataIndex: 'roomNo',
        width: 140,
        fixed: 'left',
        render: renderRoomNo,
      },
      {
        title: intl.get('app_reservation_deposit_amount'),
        dataIndex: 'deposit',
        width: 180,
        align: 'right',
        render: renderDepositItem,
      },
    ];
    // 入住日期列
    const rentDayColumns: ColumnsType<EditRentRoomInfo> = [];
    if (rentRoomInfos.length) {
      const { roomPriceList } = rentRoomInfos[0]; // 入住天价格列表
      const rentDays = roomPriceList || [];
      rentDays.forEach((rentDay, idx) => {
        const { rentDate } = rentDay;
        rentDayColumns.push({
          title: <CouponGapText text={rentDate} />,
          dataIndex: ['roomPriceList', idx],
          width: 260,
          align: 'right',
          render: (value, record) => renderPriceItem(value, record),
        });
      });
    }
    return {
      tableColumns: [...leftColumns, ...rentDayColumns],
    };
  };
  const tableInfo = getTableInfo();

  // 一键选券参数
  const getCouponParam = () => {
    return new Promise<IBatchCouponParams>((resolve) => {
      callGetState(() => {
        resolve({
          extra: {
            wyuid: mainUserId,
            couponNos: originCouponList,
          },
          rooms: rentRoomInfos.map((room) => {
            const roomPriceList = [];
            room.roomPriceList.forEach((dayPrice) => {
              if (dayPrice.isBeforeAudit) return; // 已过夜审 不参与选券
              roomPriceList.push({
                key: dayPrice.rentDateId,
                occType,
                rentDate: dayPrice.rentDate,
                roomPrice: dayPrice.inputPrice,
                couponNo: dayPrice.couponNo,
              });
            });
            return {
              indexId: room.indexId,
              roomId: room.roomId,
              roomNo: room.roomNo,
              roomTypeId: room.roomTypeId,
              roomTypeName: room.roomTypeName,
              roomPriceList,
            };
          }),
        });
      });
    });
  };

  return (
    <ContentBlock
      title={intl.get('drawer_order_editor_room_block_title')}
      className={styles.rentRoomContainer}
      extra={
        <div className={styles.extraButtonList}>
          <BatchCouponButton
            userId={mainUserId}
            channelType={currentChannelType}
            getCouponParam={getCouponParam}
            onChange={callBatchUpdateCoupon}
          />
        </div>
      }
    >
      <Table
        pagination={false}
        rowKey="indexId"
        columns={tableInfo.tableColumns}
        dataSource={rentRoomInfos}
        scroll={{ x: 800 }}
      />
      <CouponSelector ref={couponSelectorRef} />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
